import type { TFunction } from 'i18next';
import i18n from 'i18next';
import type { WorkoutType } from '../services/api';
import { workoutTypeLabel } from './sportLabel';

/** workout_types.id — Brick / multisport back-to-back session */
export const WORKOUT_TYPE_BRICK_ID = 4;
/** workout_types.id — Crosstrain / general cross-training */
export const WORKOUT_TYPE_CROSSTRAIN_ID = 5;

/** Display order: MTB directly under road cycling */
const PICKER_ORDER = [1, 2, 8, 3, 4, 5, 6, 7, 9, 10, 11, 12, 13, 14] as const;

export function sortWorkoutTypesForPicker(types: WorkoutType[]): WorkoutType[] {
  const rank = new Map<number, number>(PICKER_ORDER.map((id, index) => [id, index]));
  const seen = new Set<string>();
  return [...types].sort(
    (a, b) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999),
  ).filter(type => {
    const label = `${type.english_name ?? ''} ${type.name ?? ''}`.toLowerCase().replace(/[\s\u200c-]/g, '');
    const key = Number(type.id) === 12 || /rowing|قایقرانی/.test(label) ? 'rowing' : `id:${type.id}`;
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });
}

/** Events and legacy custom sessions remain readable, but are not new workout sports. */
export function workoutTypesForCreation(types: WorkoutType[]): WorkoutType[] {
  return sortWorkoutTypesForPicker(types).filter(type => ![6, 10].includes(Number(type.id)) && !/^(race|custom)$/i.test((type.english_name ?? '').trim()));
}

/** Locale-aware label — Brick uses i18n instead of legacy DB Persian title. */
export function getWorkoutTypeDisplayName(type: WorkoutType, t: TFunction): string {
  if (type.id === WORKOUT_TYPE_BRICK_ID) {
    return t('workoutForm.type.brick.label');
  }
  if (type.id === WORKOUT_TYPE_CROSSTRAIN_ID) {
    return t('workoutForm.type.crosstrain.label');
  }
  return workoutTypeLabel(type, i18n.language);
}

export function workoutTypeHasHelp(type: WorkoutType): boolean {
  if (Number(type.id) === WORKOUT_TYPE_BRICK_ID) return true;
  return (type.english_name ?? '').trim().toLowerCase() === 'brick';
}
